(function () {
    'use strict';

    if (typeof window === 'undefined') {
        metaDeprecatedMetricCards(module.exports, require('../../../lib/riq-moment.js'));
    } else {
        RivalIQ.apps.Insights.module('metaDeprecatedMetricCards', function (metaDeprecatedMetricCardsModule) {
            metaDeprecatedMetricCards(metaDeprecatedMetricCardsModule, window.moment);
        });
    }

    function metaDeprecatedMetricCards(exports, moment) {
        const DEPRECATED_METRIC_VISIBILITY_CUTOFF = moment.utc('2026-06-17');
        const SOURCE_WIDGETS_KEY = '__metaDeprecatedMetricCardsSourceWidgets';
        // The presence (page-level) paid/viral/nonviral breakdowns have no Meta recovery path
        // (docs/adr/0002 declines to rebuild the page-level daily series). They are pruned outright.
        // Post-level impressions (impressions_per_post, summary-metric2, post-impressions-by-day)
        // are NOT pruned: they are reconstructed via the post_media_view alias and the
        // is_from_ads breakdown (docs/adr/0001, docs/adr/0002 PR B1), so the tiles keep showing
        // numbers — only the card-level tooltip carries the deprecation note.
        //
        // NOT PART OF THE JUNE 17, 2026 WAVE — five FB Insights demographic/geo cards (page reach &
        // fans by age/gender/city/country: FacebookPageReachGenderWidget, FacebookPageReachAgeWidget,
        // AgeGenderWidget, the FB Demographics branch, and FacebookInsightsPageImpressionsAndReachLocations)
        // are backed by demographic-breakdown metrics (page_impressions_by_age_gender/city/country_unique,
        // page_fans_*) that Meta deprecated in the Sept. 2024 wave — not the June 17, 2026 cutoff this
        // module handles. Date-conditional hiding is the wrong mechanism for them: verified in production
        // (2026-06-29, NISMO Facebook Insights) they render "No data in this time period" even for a window
        // ending on/before June 17, so they are empty independent of the cutoff.
        //
        // They are ALREADY handled by the pre-existing utils.js FACEBOOK_DEPRECATED_WIDGET_IDS path, which
        // keys on the parent FB card's widgetId (fb_insights_page_reach_age / _reach_gender /
        // _impressions_and_reach_locations / _fans_locations). That also covers the inner AgeGenderWidget
        // and FB Demographics widgets, since their dashboardModel.curation.widgetId resolves to the parent
        // card id. Verified in production: the facebook_deprecated_2024_09 note ("...deprecation of these
        // metrics in Sept. 2024...") renders on the warning icon of these empty cards. So there is no gap
        // here for this module to close.
        //
        // Do NOT add the inner widget TYPE ids (insights_age_gender_distribution, insights_demographics) to
        // FACEBOOK_DEPRECATED_WIDGET_IDS — those types are shared with Instagram/LinkedIn and would mis-tag
        // live non-FB cards.
        const FACEBOOK_IMPRESSIONS_REACH_AFFECTED_SUMMARY_WIDGETS = new Set([
            'presence-impressions-paid'
        ]);
        const FACEBOOK_IMPRESSIONS_REACH_AFFECTED_METRIC_TITLES = new Set([
            'Paid',
            'Organic, Non-Viral',
            'Organic, Viral'
        ]);
        const DEPRECATED_METRIC_DESCRIPTIONS = {
            'presence-impressions-paid': 'Facebook deprecated Presence Impressions, Paid after June 17, 2026. This metric is hidden when the selected date range starts after June 17, 2026.',
            'Paid': 'Facebook deprecated Paid presence impressions after June 17, 2026. This metric is hidden when the selected date range starts after June 17, 2026.',
            'Organic, Non-Viral': 'Facebook deprecated Organic, Non-Viral presence impressions after June 17, 2026. This metric is hidden when the selected date range starts after June 17, 2026.',
            'Organic, Viral': 'Facebook deprecated Organic, Viral presence impressions after June 17, 2026. This metric is hidden when the selected date range starts after June 17, 2026.'
        };

        // The customer-facing note shown on the tooltip of any built-in card that contains a
        // metric affected by the June 17, 2026 Meta deprecation. It supersedes the older
        // "As of November 13, 2025 ..." note when one is already present (see CONTEXT.md).
        const DEPRECATED_METRIC_CARD_TOOLTIP_NOTE = 'As of June 17, 2026, due to Facebook deprecations, this metric is no longer reported by Facebook and is hidden when the selected date range starts after June 17, 2026.';
        const TOOLTIP_NOTE_SEPARATOR = '<br/><br/>';
        // Matches a pre-existing trailing "As of November 13, 2025 ..." deprecation note so it can
        // be replaced wholesale. The note is always the last section of the card description.
        const SUPERSEDED_TOOLTIP_NOTE_PATTERN = /As of November 13, 2025[\s\S]*$/;

        exports.cardDescriptionWithDeprecationNote = function (description) {
            if (!description || typeof description !== 'string') {
                return description;
            }

            // Idempotent: never stack the note if it has already been applied.
            if (description.indexOf(DEPRECATED_METRIC_CARD_TOOLTIP_NOTE) !== -1) {
                return description;
            }

            if (SUPERSEDED_TOOLTIP_NOTE_PATTERN.test(description)) {
                return description.replace(SUPERSEDED_TOOLTIP_NOTE_PATTERN, DEPRECATED_METRIC_CARD_TOOLTIP_NOTE);
            }

            return description + TOOLTIP_NOTE_SEPARATOR + DEPRECATED_METRIC_CARD_TOOLTIP_NOTE;
        };

        exports.shouldHideForTimePeriod = function (timePeriod) {
            const mainPeriodStart = timePeriod && timePeriod.mainPeriodStart;

            if (!mainPeriodStart) {
                return false;
            }

            return moment.utc(mainPeriodStart).startOf('day').isAfter(DEPRECATED_METRIC_VISIBILITY_CUTOFF, 'day');
        };

        function nextTimePeriod(widget, props) {
            if (props && props.timePeriod) {
                return props.timePeriod;
            }

            return widget && widget.getProp && widget.getProp('timePeriod');
        }

        function timePeriodChangedCutoffVisibility(widget, changed, props) {
            if (changed && Object.prototype.hasOwnProperty.call(changed, 'timePeriod')) {
                return true;
            }

            const currentTimePeriod = nextTimePeriod(widget, props);
            const previousTimePeriod = widget && widget.getProp && widget.getProp('timePeriod');

            if (!currentTimePeriod || !previousTimePeriod) {
                return false;
            }

            return exports.shouldHideForTimePeriod(currentTimePeriod) !==
                exports.shouldHideForTimePeriod(previousTimePeriod);
        }

        function recursivelyResetChildren(widget) {
            if (widget.widgets && widget.widgets.each) {
                widget.widgets.each(function (child) {
                    if (child.configuration) {
                        const existingWidgets = child.configuration._widgetsCache;
                        resetWidgetCache(child, existingWidgets);
                    } else {
                        recursivelyResetChildren(child);
                    }
                });
            }
        }

        function resetWidgetCache(widget, existingWidgets) {
            recursivelyResetChildren(widget);

            if (widget.configuration) {
                if (widget.widgetDefinitions) {
                    widget.configuration.set('definitions', widget.widgetDefinitions());
                }
                widget.configuration.resetWidgetCache();
            }

            if (existingWidgets && existingWidgets.reset && widget.getWidgets) {
                const freshWidgets = widget.getWidgets();
                const freshModels = freshWidgets && (freshWidgets.models || freshWidgets.toArray && freshWidgets.toArray());

                evictRenderedWidgets(existingWidgets);
                existingWidgets.reset(freshModels || []);
                widget.configuration._widgetsCache = existingWidgets;
                triggerTimePeriodReload(existingWidgets, widget);
            }

            if (widget.trigger) {
                widget.trigger('deprecatedMetricCards:reset', widget);
                widget.trigger('change', widget);
            }
        }

        function evictRenderedWidgets(existingWidgets) {
            if (!existingWidgets.remove) {
                return;
            }

            const existingModels = existingWidgets.models || (existingWidgets.toArray && existingWidgets.toArray()) || [];

            existingModels.forEach(existingModel => {
                existingWidgets.remove(existingModel);
            });
        }

        function triggerTimePeriodReload(widgets, parentWidget) {
            const timePeriod = parentWidget && parentWidget.getProp && parentWidget.getProp('timePeriod');
            const models = widgets.models || (widgets.toArray && widgets.toArray()) || [];

            models.forEach(model => {
                if (model.trigger) {
                    model.trigger('change:timePeriod', model, timePeriod, {});
                    model.trigger('change', model, {});
                }
            });
        }

        function afterCurrentChangeHandlers(callback) {
            setTimeout(callback, 0);
        }

        exports.resetWidgetCacheForTimePeriodChange = function (widget, changed, props) {
            if (
                timePeriodChangedCutoffVisibility(widget, changed, props) &&
                widget &&
                widget.configuration &&
                widget.configuration.resetWidgetCache
            ) {
                const existingWidgets = widget.configuration._widgetsCache;
                if (props && props.timePeriod) {
                    // processProps runs during render-time prop resolution, AFTER the model's
                    // `change:timePeriod` has already fired. Registering `widget.once('change:timePeriod', ...)`
                    // here would bind to the NEXT date change, lagging the prune visibility flip
                    // by one user action. Defer via setTimeout(0) only — that lets Backbone finish
                    // its current change cascade without waiting for a future event that's already
                    // been emitted.
                    afterCurrentChangeHandlers(function () {
                        resetWidgetCache(widget, existingWidgets);
                    });
                } else {
                    resetWidgetCache(widget, existingWidgets);
                }
            }
        };

        function cloneDefinition(value) {
            if (Array.isArray(value)) {
                return value.map(cloneDefinition);
            }

            if (!value || typeof value !== 'object') {
                return value;
            }

            return Object.keys(value).reduce((clone, key) => {
                clone[key] = cloneDefinition(value[key]);
                return clone;
            }, {});
        }

        function sourceWidgets(widgets) {
            return (widgets && widgets[SOURCE_WIDGETS_KEY]) || widgets || [];
        }

        function rememberSource(prunedWidgets, widgets) {
            Object.defineProperty(prunedWidgets, SOURCE_WIDGETS_KEY, {
                configurable: true,
                enumerable: false,
                value: cloneDefinition(sourceWidgets(widgets))
            });

            return prunedWidgets;
        }

        function pruneCollection(collection, shouldRemove) {
            return (collection || []).filter(item => !shouldRemove(item));
        }

        function descriptionWithDeprecatedMetric(metric, description) {
            if (!metric || !description) {
                return metric;
            }

            const descriptions = [metric.description, description].filter(Boolean);
            return Object.assign({}, metric, {
                description: descriptions.join(' ')
            });
        }

        function annotateMetricDefinition(metric, descriptionKey) {
            return descriptionWithDeprecatedMetric(metric, DEPRECATED_METRIC_DESCRIPTIONS[descriptionKey]);
        }

        function annotateSummaryWidget(summaryWidget) {
            if (!FACEBOOK_IMPRESSIONS_REACH_AFFECTED_SUMMARY_WIDGETS.has(summaryWidget.instanceId)) {
                return summaryWidget;
            }

            const props = Object.assign({}, summaryWidget.props || {});

            if (props.metrics) {
                props.metrics = props.metrics.map(metric => {
                    return annotateMetricDefinition(metric, summaryWidget.instanceId);
                });
            }

            return Object.assign({}, summaryWidget, {
                props: props
            });
        }

        function annotateImpressionsReachWidget(widget) {
            const props = Object.assign({}, widget.props || {});

            if (props.summaryWidgets) {
                props.summaryWidgets = props.summaryWidgets.map(annotateSummaryWidget);
            }

            if (props.metrics) {
                props.metrics = props.metrics.map(metric => {
                    return annotateMetricDefinition(metric, metric.title);
                });
            }

            return Object.assign({}, widget, {
                props: props
            });
        }

        exports.pruneFacebookImpressionsReachWidgets = function (widgets, timePeriod) {
            const originalWidgets = sourceWidgets(widgets);
            widgets = originalWidgets.map(annotateImpressionsReachWidget);

            if (!exports.shouldHideForTimePeriod(timePeriod)) {
                return widgets;
            }

            const pruned = (widgets || []).map(widget => {
                const props = widget.props || {};
                const prunedWidget = Object.assign({}, widget, {
                    props: Object.assign({}, props)
                });

                if (props.summaryWidgets) {
                    prunedWidget.props.summaryWidgets = pruneCollection(props.summaryWidgets, summaryWidget => {
                        return FACEBOOK_IMPRESSIONS_REACH_AFFECTED_SUMMARY_WIDGETS.has(summaryWidget.instanceId);
                    });
                }

                if (props.metrics) {
                    prunedWidget.props.metrics = pruneCollection(props.metrics, metric => {
                        return FACEBOOK_IMPRESSIONS_REACH_AFFECTED_METRIC_TITLES.has(metric.title);
                    });

                    if (
                        prunedWidget.instanceId === 'reach-over-time' &&
                        prunedWidget.props.metrics.length === 0 &&
                        prunedWidget.props.metric
                    ) {
                        prunedWidget.props.metrics = [prunedWidget.props.metric];
                        delete prunedWidget.props.metric;
                    }
                }

                return prunedWidget;
            }).filter(widget => {
                return !widget.props ||
                    !(
                        Array.isArray(widget.props.summaryWidgets) &&
                        widget.props.summaryWidgets.length === 0
                    ) &&
                    !(
                        Array.isArray(widget.props.metrics) &&
                        widget.props.metrics.length === 0 &&
                        !widget.props.metric
                    );
            });

            return rememberSource(pruned, originalWidgets);
        };

    }
}());
